"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createJFD = void 0;
const observer_util_1 = require("@nx-js/observer-util");
const jfd_def_1 = require("./jfd-def");
const jfd_fields_1 = require("./jfd-fields");
const jfd_images_1 = require("./jfd-images");
const jfd_values_1 = require("./jfd-values");
const types_1 = require("./types");
function createJFD(jfd) {
    return new JFDImpl(jfd);
}
exports.createJFD = createJFD;
class JFDImpl {
    constructor(jfd) {
        this.importErrors = [];
        // converts from PartialJFDDef to a complete JFDDef
        this.jfdDef = jfd_def_1.newJFDDef(jfd);
        // create the necessary JFDField instances
        this.fields = this.makeFields(this.jfdDef);
        // apply rules
        const obs = observer_util_1.observable(this);
        this.jfdDef.rules.forEach((rule) => {
            observer_util_1.observe(() => {
                rule(obs);
            });
        });
        this.images = [];
        this.imageErrors = [];
        observer_util_1.observe(() => {
            this.imageErrors = [];
            this.imageErrors = jfd_images_1.validateImages(obs);
            this.jfdDef.imageRules.forEach((rule) => {
                rule(obs);
            });
        });
        // return observed 'this' so that the new object can be reactive
        return obs;
    }
    makeFields(jfd) {
        const result = {};
        // create a JFDField instance for each field
        Object.keys(jfd.fields).forEach((fieldName) => {
            const fieldDef = this.getFieldDef(fieldName);
            switch (fieldDef.getType()) {
                case 'SONS':
                    result[fieldName] = this.makeNSField(fieldDef);
                    break;
                case 'SOS':
                    result[fieldName] = JFDImpl.makeSField(fieldDef);
                    break;
                case 'MONS':
                    result[fieldName] = [];
                    const monsField = result[fieldName];
                    for (let i = 0; i < fieldDef.minOccurrences; i++) {
                        monsField.push(this.makeNSField(fieldDef));
                    }
                    break;
                case 'MOS':
                    result[fieldName] = [];
                    const mosField = result[fieldName];
                    for (let i = 0; i < fieldDef.minOccurrences; i++) {
                        mosField.push(JFDImpl.makeSField(fieldDef));
                    }
                    break;
            }
        });
        return result;
    }
    makeNSField(fieldDef) {
        const result = observer_util_1.observable(jfd_fields_1.newJFDField(fieldDef));
        observer_util_1.observe(() => {
            result.error = result.validate();
        });
        return result;
    }
    static makeSField(fieldDef) {
        // recursive call, this ensure that we can have a 'sub-context' for nested
        // rules (subfields.rules)
        const nestedJFD = createJFD({
            fields: fieldDef.subFields.fields,
            rules: fieldDef.subFields.rules
        });
        // small hack to fix fullName for these fields
        //
        // in theory, we should do this in newJFDFieldDef() (in jfd-def.ts), but
        // given the recursive call it would need some changes to the JFDImpl
        // constructor, and some adjustments to `makeSField` and its callers
        // which is more complicated.
        const fields = nestedJFD.fields;
        Object.values(fields).forEach((jfdField) => {
            jfdField.fullName = `${fieldDef.name}.${jfdField.name}`;
        });
        return fields;
    }
    onInit() {
        for (const rule of this.jfdDef.onInit) {
            rule(this);
        }
    }
    onCreate() {
        for (const rule of this.jfdDef.onCreate) {
            rule(this);
        }
    }
    onLoadCaptureStationInfo() {
        for (const rule of this.jfdDef.onLoadCaptureStationInfo) {
            rule(this);
        }
    }
    onGenerateEBTSFile() {
        this.convertToUpperCase();
        for (const rule of this.jfdDef.onGenerateEBTSFile) {
            rule(this);
        }
    }
    onImport() {
        // reset existing errors, then execute the rules
        this.importErrors = [];
        for (const rule of this.jfdDef.onImport) {
            rule(this);
        }
    }
    get bookingNumber() {
        return this.getValue(this.jfdDef.bookingNumberFieldName);
    }
    set bookingNumber(val) {
        this.setValue(this.jfdDef.bookingNumberFieldName, val);
    }
    getValue(fieldName, index) {
        return this.getField(fieldName, index).value;
    }
    setValue(fieldName, value, index, setReadOnly = true) {
        const field = this.getField(fieldName, index);
        if (setReadOnly || !field.readOnly) {
            field.value = value;
        }
    }
    setValues(values, setReadOnly = true, removeExtraOccurrences = true) {
        Object.entries(values).forEach(([fieldName, value]) => {
            const fieldDef = this.getFieldDef(fieldName);
            switch (fieldDef.getType()) {
                case 'SONS':
                    const sonsValue = value;
                    this.setValue(fieldName, sonsValue, undefined, setReadOnly);
                    break;
                case 'SOS':
                    const sosValue = value;
                    Object.entries(sosValue).forEach(([subfieldName, subfieldValue]) => {
                        this.setValue(`${fieldName}.${subfieldName}`, subfieldValue, undefined, setReadOnly);
                    });
                    break;
                case 'MONS':
                    const monsValue = value;
                    monsValue.forEach((value, idx) => {
                        this.createOccurrence(fieldName, idx);
                        this.setValue(fieldName, value, idx, setReadOnly);
                    });
                    break;
                case 'MOS':
                    const mosValue = value;
                    mosValue.forEach((value, idx) => {
                        this.createOccurrence(fieldName, idx);
                        Object.entries(value).forEach(([subfieldName, subfieldValue]) => {
                            this.setValue(`${fieldName}.${subfieldName}`, subfieldValue, idx, setReadOnly);
                        });
                    });
                    break;
            }
            // remove extra elements for MONS and MOS
            if (removeExtraOccurrences && fieldDef.hasMultipleOccurrences()) {
                const newVal = this.fields[fieldName];
                const moVal = value;
                for (let i = moVal.length; i < newVal.length; i++) {
                    newVal.pop();
                }
            }
        });
    }
    cleanValues(values) {
        const result = {};
        Object.entries(values).forEach(([fieldName, value]) => {
            if (!this.hasFieldDef(fieldName)) {
                return;
            }
            const fieldDef = this.getFieldDef(fieldName);
            switch (fieldDef.getType()) {
                case 'SONS':
                    if (jfd_values_1.isSONS(value)) {
                        result[fieldName] = value;
                    }
                    break;
                case 'SOS':
                    if (!jfd_values_1.isSOS(value)) {
                        return;
                    }
                    result[fieldName] = {};
                    Object.entries(value).forEach(([subfieldName, subfieldValue]) => {
                        if (this.hasFieldDef(`${fieldName}.${subfieldName}`)) {
                            const sosValue = result[fieldName];
                            sosValue[subfieldName] = subfieldValue;
                        }
                    });
                    break;
                case 'MONS':
                    if (!jfd_values_1.isMONS(value)) {
                        return;
                    }
                    result[fieldName] = [];
                    for (const e of value) {
                        const monsValue = result[fieldName];
                        monsValue.push(e);
                    }
                    break;
                case 'MOS':
                    if (!jfd_values_1.isMOS(value)) {
                        return;
                    }
                    result[fieldName] = [];
                    const mosValue = result[fieldName];
                    for (const e of value) {
                        const el = {};
                        Object.entries(e).forEach(([subfieldName, subfieldValue]) => {
                            if (this.hasFieldDef(`${fieldName}.${subfieldName}`)) {
                                el[subfieldName] = subfieldValue;
                            }
                        });
                        mosValue.push(el);
                    }
                    break;
            }
        });
        return result;
    }
    resetFields() {
        this.fields = this.makeFields(this.jfdDef);
    }
    getField(fieldName, index) {
        // check that the field exists
        this.getFieldDef(fieldName);
        const [field, subfield] = fieldName.split('.');
        const fieldDef = this.getFieldDef(field);
        // check for subfields, we can't return the root object for SOS or MOS
        if (fieldDef.hasSubFields() && !subfield) {
            const idxMsg = index ? ` index=${index}` : '';
            throw new Error(`Cannot access root JFDField. field=${fieldName}${idxMsg}`);
        }
        if (fieldDef.hasMultipleOccurrences()) {
            if (index === undefined) {
                // check that we were given an index if the field has multiple occurrences
                throw new Error(`Missing index for multiple occurrences field. field=${fieldName}`);
            }
            if (index >= fieldDef.maxOccurrences) {
                // check for max occurrences
                throw new Error(`Out of bounds (max occurrences). field=${fieldName}`);
            }
        }
        switch (fieldDef.getType()) {
            case 'SONS':
                return this.fields[field];
            case 'SOS':
                const sos = this.fields[field];
                return sos[subfield];
            case 'MONS':
                const mons = this.fields[field];
                if (mons[index] === undefined) {
                    throw new Error(`Out of bounds: field=${fieldName} index=${index} currentEntries=${mons.length}`);
                }
                else {
                    return mons[index];
                }
            case 'MOS':
                const mos = this.fields[field];
                if (mos[index] === undefined) {
                    throw new Error(`Out of bounds: field=${fieldName} index=${index} currentEntries=${mos.length}`);
                }
                else {
                    return mos[index][subfield];
                }
        }
    }
    getFields() {
        const result = [];
        Object.entries(this.jfdDef.fields).forEach(([fieldName, fieldDef]) => {
            switch (fieldDef.getType()) {
                case 'SONS':
                    const sons = this.fields[fieldName];
                    result.push([fieldDef.name, sons.value, undefined]);
                    break;
                case 'SOS':
                    const sos = this.fields[fieldName];
                    Object.entries(sos).forEach(([subfieldName, subfield]) => {
                        result.push([`${fieldDef.name}.${subfieldName}`, subfield.value, undefined]);
                    });
                    break;
                case 'MONS':
                    const mons = this.fields[fieldName];
                    mons.forEach((field, idx) => {
                        result.push([fieldDef.name, field.value, idx]);
                    });
                    break;
                case 'MOS':
                    const mos = this.fields[fieldName];
                    mos.forEach((field, idx) => {
                        Object.entries(field).forEach(([subfieldName, subfield]) => {
                            result.push([`${fieldDef.name}.${subfieldName}`, subfield.value, idx]);
                        });
                    });
                    break;
            }
        });
        return result;
    }
    getModalitesAvailable() {
        const modalitiesAvailable = [];
        const { modalities: jfdModalities } = this.jfdDef.images;
        for (const modality in jfdModalities) {
            if (!modality.includes('+')) {
                modalitiesAvailable.push(modality);
            }
        }
        return modalitiesAvailable;
    }
    getModalityDef(modalityName) {
        const defaultDef = {
            maxOccurrences: 0,
            minOccurrences: 0
        };
        let exists = false;
        let modalityDef = {
            def: { ...defaultDef }
        };
        const { modalities } = this.jfdDef.images;
        for (const modalityIdx in modalities) {
            const modalitiesDefs = modalityIdx.split('+');
            if (modalitiesDefs.includes(modalityName)) {
                const modalityRules = modalities[modalityIdx];
                const maxOccurrences = Number(modalityRules === null || modalityRules === void 0 ? void 0 : modalityRules.maxOccurrences);
                const minOccurrences = Number(modalityRules === null || modalityRules === void 0 ? void 0 : modalityRules.minOccurrences);
                const type = modalityRules === null || modalityRules === void 0 ? void 0 : modalityRules.type;
                if (modalitiesDefs.length > 1) {
                    modalityDef.sharedDefs = {
                        modalities: modalitiesDefs.filter((modality) => modality !== modalityName),
                        defs: {
                            maxOccurrences,
                            minOccurrences
                        }
                    };
                }
                exists = true;
                modalityDef.def = {
                    ...modalityDef.def,
                    ...(maxOccurrences !== 0 ? { maxOccurrences } : {}),
                    ...(minOccurrences !== 0 ? { minOccurrences } : {}),
                    ...(typeof type !== 'undefined' ? { type } : {})
                };
            }
        }
        if (!exists) {
            throw new types_1.ModalityDefNotFoundError(modalityName);
        }
        return modalityDef;
    }
    getBioTypeDef(bioType) {
        let rules = {
            required: false
        };
        const { bioTypes } = this.jfdDef.images;
        if (typeof bioTypes[bioType] === 'undefined') {
            throw Error('BioTypeDef does not exists: ' + bioType);
        }
        rules = {
            ...rules,
            ...bioTypes[bioType]
        };
        return rules;
    }
    hasFieldDef(fieldName) {
        const [field, subfield] = fieldName.split('.');
        if (!(field in this.jfdDef.fields)) {
            return false;
        }
        const topField = this.jfdDef.fields[field];
        return !(subfield && !(subfield in topField.subFields.fields));
    }
    hasOccurrence(fieldName, index) {
        // check that the field exists
        this.getFieldDef(fieldName);
        const [field, subfield] = fieldName.split('.');
        const fieldDef = this.getFieldDef(field);
        // check for subfields, we can't return the root object for SOS or MOS
        if (fieldDef.hasSubFields() && !subfield) {
            const idxMsg = index ? ` index=${index}` : '';
            throw new Error(`Cannot access root JFDField. field=${fieldName}${idxMsg}`);
        }
        if (index >= fieldDef.maxOccurrences) {
            // check for max occurrences
            throw new Error(`Out of bounds (max occurrences). field=${fieldName}`);
        }
        const fieldType = fieldDef.getType();
        if (['MOS', 'MONS'].includes(fieldType)) {
            const multipleOccurrenceField = this.fields[field];
            return multipleOccurrenceField[index] !== undefined;
        }
        else {
            throw new Error(`Cannot procces a not multi occurrence field. field=${fieldName}`);
        }
    }
    getFieldDef(fieldName) {
        const [field, subfield] = fieldName.split('.');
        if (!(field in this.jfdDef.fields)) {
            throw new types_1.FieldDefNotFoundError(fieldName);
        }
        const topField = this.jfdDef.fields[field];
        if (subfield) {
            if (subfield in topField.subFields.fields) {
                return topField.subFields.fields[subfield];
            }
            else {
                throw new types_1.FieldDefNotFoundError(fieldName);
            }
        }
        else {
            return topField;
        }
    }
    numEntries(fieldName) {
        const [field] = fieldName.split('.');
        const fieldDef = this.getFieldDef(field);
        if (fieldDef.hasMultipleOccurrences()) {
            const jfdField = this.fields[field];
            return jfdField.length;
        }
        else {
            throw new Error(`Cannot retrieve number of entries (single occurrence field). field=${fieldName}`);
        }
    }
    createOccurrence(fieldName, index) {
        // check that the field exists
        this.getFieldDef(fieldName);
        const [field] = fieldName.split('.');
        const fieldDef = this.getFieldDef(field);
        if (!fieldDef.hasMultipleOccurrences()) {
            throw new Error('Trying to create occurrence for single-occurrence field.');
        }
        switch (fieldDef.getType()) {
            case 'MONS':
                const mons = this.fields[field];
                if (mons[index] === undefined) {
                    mons[index] = this.makeNSField(fieldDef);
                }
                break;
            case 'MOS':
                const mos = this.fields[field];
                if (mos[index] === undefined) {
                    mos[index] = JFDImpl.makeSField(fieldDef);
                }
                break;
        }
    }
    removeField(fieldName, index) {
        const [field] = fieldName.split('.');
        const fieldDef = this.getFieldDef(field);
        if (!fieldDef.hasMultipleOccurrences()) {
            throw new Error('Cannot remove single occurrence fields.');
        }
        const arr = this.fields[field];
        if (index >= arr.length) {
            throw new Error(`Out of bounds. field=${fieldName} index=${index}`);
        }
        else {
            // We can't use Array.splice here since it causes some conflicts with
            // observer-util/jfd: the related rules are called twice, and we receive
            // an array with an invalid state in the first call
            //
            // e.g. if we remove the first item in a field with two occurrences, the
            // rule receives an array like:
            // - 1st call: arr=[<empty>, JFDField] arr.length=2
            // - 2nd call: arr=[JFDField] arr.length=1
            this.fields[field] = arr.filter((e, idx) => idx !== index);
        }
    }
    removeFields(fieldNames, index) {
        // get the field (i.e. remove the subfield part)
        const fields = fieldNames.map((field) => field.split('.')[0]);
        // remove potential duplicates
        const uniqueFields = [...new Set(fields)];
        // and to the trash!
        uniqueFields.forEach((field) => this.removeField(field, index));
    }
    setRequired(fieldName, value, overrideSubFields = true) {
        const fieldDef = this.getFieldDef(fieldName);
        // set value on the JFDFieldDef so that new occurrences inherit it
        fieldDef.required = value;
        if (fieldDef.hasSubFields() && overrideSubFields) {
            Object.entries(fieldDef.subFields.fields).forEach(([_, subfieldDef]) => {
                subfieldDef.required = value;
            });
        }
        // set value on existing JFDField's
        const parentField = fieldName.split('.')[0];
        if (this.getFieldDef(parentField).hasMultipleOccurrences()) {
            for (let i = 0; i < this.numEntries(parentField); i++) {
                this.setRequiredInFields(fieldDef, fieldName, value, i, overrideSubFields);
            }
        }
        else {
            this.setRequiredInFields(fieldDef, fieldName, value, undefined, overrideSubFields);
        }
    }
    setRequiredInFields(fieldDef, fieldName, value, idx, overrideSubFields = true) {
        if (fieldDef.hasSubFields()) {
            if (overrideSubFields) {
                for (const subfield in fieldDef.subFields.fields) {
                    if (fieldDef.subFields.fields.hasOwnProperty(subfield)) {
                        this.getField(`${fieldName}.${subfield}`, idx).required = value;
                    }
                }
            }
        }
        else {
            this.getField(fieldName, idx).required = value;
        }
    }
    getValues(typeList) {
        this.convertToUpperCase();
        return this.getFieldAttr((field) => {
            if ((typeof typeList === 'undefined' || this.isRecordType(field, typeList)) && field.isApplicable()) {
                return field.value;
            }
        });
    }
    getErrors(typeList) {
        return this.getFieldAttr((field) => {
            if ((typeof typeList === 'undefined' || this.isRecordType(field, typeList)) && field.isApplicable()) {
                return field.error || undefined;
            }
        });
    }
    isRecordType(field, typeList) {
        // return all fields without EBTS attribute (possibly generated) fields
        if (!field.EBTS) {
            return true;
        }
        else if (field.EBTS.index.match(/\./)) {
            const recordType = +field.EBTS.index.split('.')[0];
            return typeList.includes(recordType);
        }
        else {
            return false;
        }
    }
    /**
     * @param getter
     * A function that receives a JFDField and can return
     * 1) a string, that is included in the return value
     * 2) void, in which case the field is omitted from the return value
     */
    getFieldAttr(getter) {
        const result = {};
        Object.entries(this.jfdDef.fields).forEach(([fieldName, fieldDef]) => {
            switch (fieldDef.getType()) {
                case 'SONS':
                    const sons = this.fields[fieldName];
                    const sonsValue = getter(sons);
                    if (sonsValue !== undefined) {
                        result[fieldName] = sonsValue;
                    }
                    break;
                case 'SOS':
                    const sos = this.fields[fieldName];
                    const sosValue = this.getSubValues(sos, getter);
                    if (Object.keys(sosValue).length > 0) {
                        result[fieldName] = sosValue;
                    }
                    break;
                case 'MONS':
                    {
                        const fields = this.fields[fieldName];
                        const allValues = fields.map(getter);
                        const isEmpty = allValues.every((val) => val === undefined);
                        if (!isEmpty) {
                            result[fieldName] = allValues;
                        }
                    }
                    break;
                case 'MOS':
                    {
                        const fields = this.fields[fieldName];
                        const allValues = fields.map((field) => this.getSubValues(field, getter));
                        const isEmpty = allValues.every((val) => Object.keys(val).length === 0);
                        if (!isEmpty) {
                            result[fieldName] = allValues;
                        }
                    }
                    break;
            }
        });
        return result;
    }
    getSubValues(field, getter) {
        const entries = 
        // entries are tuples [key, value] for each attribute in `field`
        Object.entries(field)
            // call `getter` on each subfield
            .map(([subfieldName, subfield]) => [subfieldName, getter(subfield)]);
        const result = {};
        entries.forEach(([field, value]) => {
            // remove empty values
            if (field !== undefined && value !== undefined) {
                result[field] = value;
            }
        });
        // note that this might be an empty object ({}) if we filtered everything
        return result;
    }
    /**
     * Fill-in all fields with mock data
     * @param fieldNames - An optional JFL to limit the fields to fill in with mock data
     */
    fillInMockData(fieldNames) {
        // Fallback to use all fields
        const include = new Set(fieldNames);
        // Get concrete fields by name
        const concrete = this.getFields()
            // Skip fields that already have a value
            .filter(([, value]) => value === '')
            .map(([fieldName, _, index]) => [fieldName, index]);
        // Filter fields according to the names in the include-list
        let filtered;
        if (include.size === 0) {
            filtered = concrete;
        }
        else {
            filtered = concrete.filter(([fieldName]) => {
                return include.has(fieldName);
            });
        }
        // Gather all fields, subfields and occurrences
        filtered
            // Map names to actual fields
            .map(([fieldName, index]) => this.getField(fieldName, index))
            // Skip readonly
            .filter((field) => !field.readOnly)
            .forEach((field) => {
            field.fillInMockData();
        });
    }
    convertToUpperCase() {
        this.getFieldAttr((field) => {
            if (field.convertToUpperCase) {
                field.value = field.value.toUpperCase();
            }
        });
    }
    setImagesDef(def) {
        this.jfdDef.images = jfd_def_1.newJFDImagesDef(def);
    }
    setImages(images) {
        this.images = images;
    }
    getImages(filter) {
        return this.images
            .filter((image) => {
            if (filter.modality) {
                return jfd_images_1.ModalityBioTypeMap[filter.modality].includes(image.bioType);
            }
            else {
                return true;
            }
        })
            .filter((image) => {
            if (filter.bioType) {
                return filter.bioType === image.bioType;
            }
            else {
                return true;
            }
        });
    }
    getModalityGroups(modality) {
        const result = new Map();
        for (const image of this.images) {
            if (jfd_images_1.getModality(image.bioType) !== modality) {
                continue;
            }
            if (!result.has(image.groupId)) {
                result.set(image.groupId, {
                    modality,
                    groupId: image.groupId,
                    images: []
                });
            }
            result.get(image.groupId).images.push(image);
        }
        return Array.from(result.values());
    }
    setImageError(error) {
        this.imageErrors.push(error);
    }
    getImageErrors() {
        return this.imageErrors;
    }
}
//# sourceMappingURL=jfd.js.map